/**
 * El código de descuento del closer, guardado entre pantallas.
 *
 * Antes vivía solo en el estado de `Auth`, porque el registro y el cobro eran
 * la misma pantalla. Al separarlos —registro primero, checkout después— ese
 * estado se pierde en el salto, y con él el descuento que le prometieron a la
 * persona en el enlace... y la comisión del closer.
 *
 * Se usa `localStorage` y no `sessionStorage` a propósito: el enlace del closer
 * llega por WhatsApp y se abre en el navegador interno de la app, que la gente
 * cierra y reabre. Con la sesión de pestaña, el código se evapora justo en el
 * público al que va dirigido.
 *
 * El plazo de 30 días no es arbitrario: es el tiempo en que un código de venta
 * sigue teniendo sentido. Más allá, alguien que vuelve medio año después
 * arrastraría un descuento que ya nadie recuerda haberle ofrecido.
 *
 * Esto es una comodidad, no la fuente de la verdad. La atribución firme del
 * closer va en el registro del usuario, del lado del servidor.
 */

const LLAVE = 'vondi.closer_code';
const DIAS_DE_VIDA = 30;

/**
 * Aviso de que el código ya está guardado.
 *
 * Hace falta porque el código se guarda **después** de validarlo contra el
 * servidor, mientras que las pantallas lo leen al montarse. Sin este aviso,
 * quien abre `/signup?code=ANA15` monta el formulario antes de que la
 * validación termine, lee «no hay código» y nunca vuelve a mirar: el descuento
 * está guardado y aplicado, pero la persona no lo ve por ningún lado. Y lo que
 * no se ve, en una pantalla de venta, es como si no existiera.
 */
export const EVENTO_CODIGO = 'vondi:codigo-closer';

/**
 * @param {{code: string, discount_percent?: number, closer_name?: string}} datos
 *        Lo que devolvió la validación del código.
 */
export function guardarCodigo(datos) {
  if (!datos?.code) return;

  try {
    localStorage.setItem(LLAVE, JSON.stringify({
      code: datos.code,
      discount_percent: datos.discount_percent ?? null,
      closer_name: datos.closer_name ?? null,
      guardado: Date.now(),
    }));

    window.dispatchEvent(new CustomEvent(EVENTO_CODIGO));
  } catch (e) {
    /* Modo privado o almacenamiento lleno: se sigue sin código guardado, que es
       peor experiencia pero no un error que deba detener nada. */
  }
}

/**
 * @returns {null|{code: string, discount_percent: number|null, closer_name: string|null}}
 *          null si no hay código o si ya caducó.
 */
export function leerCodigo() {
  try {
    const crudo = localStorage.getItem(LLAVE);
    if (!crudo) return null;

    const dato = JSON.parse(crudo);
    if (!dato?.code) return null;

    const edadEnDias = (Date.now() - (dato.guardado ?? 0)) / 86400000;
    if (edadEnDias > DIAS_DE_VIDA) {
      limpiarCodigo();
      return null;
    }

    return {
      code: dato.code,
      discount_percent: dato.discount_percent ?? null,
      closer_name: dato.closer_name ?? null,
    };
  } catch (e) {
    return null;
  }
}

export function limpiarCodigo() {
  try {
    localStorage.removeItem(LLAVE);
  } catch (e) {
    /* Nada que hacer, y nada que se rompa por no poder borrarlo. */
  }
}
